import {
  AddOrderNoteRequest,
  AdminOrderDetail,
  AdminOrderFilters,
  AdminOrderStatistics,
  AssignOrderRequest,
  BulkUpdateOrderStatusRequest,
  OrderDetails,
  OrderStatusDto,
  PaginatedAdminOrdersResponse,
  PaginatedHistoryResponse,
  UpdateOrderComprehensiveRequest,
  UpdateOrderPaymentStatusRequest,
  UpdateOrderStatusRequest,
} from "../types/adminOrders";
import {
  normalizeOrderFinancialCollection,
  normalizeOrderFinancialFields,
} from "../utils/orderFinancials";

import apiClient, { withLongTimeout } from "./apiClient";

const BASE_URL = "/admin/orders";

export const adminOrderService = {
  // Get all orders with filters, sorting, and pagination
  async getOrders(
    filters: AdminOrderFilters,
  ): Promise<PaginatedAdminOrdersResponse> {
    const queryParams = new URLSearchParams();

    if (filters.search) queryParams.append("search", filters.search);
    if (filters.status) queryParams.append("status", filters.status);
    if (filters.paymentStatus)
      queryParams.append("paymentStatus", filters.paymentStatus);
    if (filters.startDate) queryParams.append("startDate", filters.startDate);
    if (filters.endDate) queryParams.append("endDate", filters.endDate);
    if (filters.customerId)
      queryParams.append("customerId", filters.customerId);
    if (filters.assignedTo)
      queryParams.append("assignedTo", filters.assignedTo);
    if (filters.minAmount)
      queryParams.append("minAmount", filters.minAmount.toString());
    if (filters.maxAmount)
      queryParams.append("maxAmount", filters.maxAmount.toString());
    if (filters.pickUpLocation)
      queryParams.append("pickUpLocation", filters.pickUpLocation);
    if (filters.includeArchived !== undefined)
      queryParams.append("includeArchived", filters.includeArchived.toString());
    if (filters.archivedOnly !== undefined)
      queryParams.append("archivedOnly", filters.archivedOnly.toString());
    if (filters.includeDeleted !== undefined)
      queryParams.append("includeDeleted", filters.includeDeleted.toString());
    if (filters.sortBy) queryParams.append("sortBy", filters.sortBy);
    if (filters.sortDescending !== undefined)
      queryParams.append("sortDescending", filters.sortDescending.toString());
    if (filters.pageNumber)
      queryParams.append("pageNumber", filters.pageNumber.toString());
    if (filters.pageSize)
      queryParams.append("pageSize", filters.pageSize.toString());

    const response = await apiClient.get(
      `${BASE_URL}?${queryParams.toString()}`,
    );
    return {
      ...response.data.data,
      items: normalizeOrderFinancialCollection(response.data.data.items || []),
    };
  },

  // Get detailed information about a specific order
  async getOrderDetail(orderNumber: string): Promise<AdminOrderDetail> {
    const response = await apiClient.get(`${BASE_URL}/${orderNumber}`);
    return normalizeOrderFinancialFields(response.data.data);
  },

  // Update order status
  async updateOrderStatus(
    orderNumber: string,
    request: UpdateOrderStatusRequest,
  ): Promise<boolean> {
    const response = await apiClient.put(
      `${BASE_URL}/${orderNumber}/status`,
      request,
    );
    return response.data.data;
  },

  // Update order payment status
  async updateOrderPaymentStatus(
    orderNumber: string,
    request: UpdateOrderPaymentStatusRequest,
  ): Promise<boolean> {
    const response = await apiClient.put(
      `${BASE_URL}/${orderNumber}/payment-status`,
      request,
    );
    return response.data.data;
  },

  // Assign order to admin user
  async assignOrder(
    orderNumber: string,
    request: AssignOrderRequest,
  ): Promise<boolean> {
    const response = await apiClient.put(
      `${BASE_URL}/${orderNumber}/assign`,
      request,
    );
    return response.data.data;
  },

  // Add note to order
  async addOrderNote(
    orderNumber: string,
    request: AddOrderNoteRequest,
  ): Promise<boolean> {
    const response = await apiClient.post(
      `${BASE_URL}/${orderNumber}/notes`,
      request,
    );
    return response.data.data;
  },

  // Bulk update order statuses
  async bulkUpdateOrderStatus(
    request: BulkUpdateOrderStatusRequest,
  ): Promise<boolean> {
    const response = await apiClient.put(`${BASE_URL}/bulk-status`, request, withLongTimeout());
    return response.data.data;
  },

  // Get order statistics
  async getOrderStatistics(): Promise<AdminOrderStatistics> {
    const response = await apiClient.get(`${BASE_URL}/statistics`);
    return response.data.data;
  },

  // Get order statistics by date range
  async getOrderStatisticsByDateRange(
    startDate: string,
    endDate: string,
  ): Promise<AdminOrderStatistics> {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
    });

    const response = await apiClient.get(
      `${BASE_URL}/statistics/range?${queryParams.toString()}`,
    );
    return response.data.data;
  },

  // Comprehensive order update - update any order field
  async updateOrderComprehensive(
    orderId: string,
    request: UpdateOrderComprehensiveRequest,
  ): Promise<boolean> {
    const response = await apiClient.put(`${BASE_URL}/${orderId}`, request);
    return response.data.data;
  },

  // Get order statuses from server
  async getOrderStatuses(): Promise<OrderStatusDto[]> {
    const response = await apiClient.get("/admin/order-statuses");
    return response.data.data.statuses;
  },

  // Get detailed order information
  async getOrderDetails(orderId: string): Promise<OrderDetails> {
    const response = await apiClient.get(`${BASE_URL}/${orderId}`);
    return normalizeOrderFinancialFields(response.data.data);
  },

  // Get order history (paginated) - using order ID
  async getOrderHistory(
    orderId: string,
    pageNumber = 1,
    pageSize = 20,
  ): Promise<PaginatedHistoryResponse> {
    const response = await apiClient.get(
      `/admin/history/entity/Order/${orderId}`,
      {
        params: { pageNumber, pageSize },
      },
    );
    return response.data.data;
  },

  // Assign cargo company to order
  async assignCargoCompany(
    orderId: string,
    cargoCompanyId: string | null,
  ): Promise<AdminOrderDetail> {
    const response = await apiClient.put(
      `${BASE_URL}/${orderId}/cargo-company`,
      { cargoCompanyId: cargoCompanyId || null },
    );
    return normalizeOrderFinancialFields(response.data.data);
  },

  // Soft delete an order
  async deleteOrder(orderId: string): Promise<boolean> {
    const response = await apiClient.delete(`${BASE_URL}/${orderId}`);
    return response.data.data;
  },

  // Restore a soft-deleted order
  async restoreOrder(orderId: string): Promise<boolean> {
    const response = await apiClient.put(`${BASE_URL}/${orderId}/restore`);
    return response.data.data;
  },

  // Archive an order
  async archiveOrder(orderId: string): Promise<boolean> {
    const response = await apiClient.put(`${BASE_URL}/${orderId}/archive`);
    return response.data.data;
  },

  // Unarchive an order
  async unarchiveOrder(orderId: string): Promise<boolean> {
    const response = await apiClient.put(`${BASE_URL}/${orderId}/unarchive`);
    return response.data.data;
  },

  // Verify or reject manual payment (for Alif/DC Bank)
  async verifyManualPayment(
    orderId: string,
    isVerified: boolean,
    notes?: string,
  ): Promise<boolean> {
    const response = await apiClient.post(
      `${BASE_URL}/${orderId}/verify-payment`,
      { 
        orderId: orderId, // Required by backend DTO
        isVerified, 
        notes: notes || null 
      },
    );
    return response.data.data;
  },
};
